import { Injectable, inject } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { CookieName } from '../browser/cookie/cookie.models';
import { CookieService } from '../browser/cookie/cookie.service';
import { UrlService } from '../navigation/url.service';
import { ProductService } from '../products/product.service';
import { LastKnownProductConfig } from './last-known-product.client-config';

/**
 * @stable
 */
export interface LastKnownProduct {
    /** Homepage url of product */
    url: string;
    /** Product name */
    name: string | 'unknown';
    /** Indicates previous product name */
    previous: string | 'unknown';
    /** Platform product id */
    platformProductId: string;
}

/**
 * @whatItDoes Provides access to last known product
 *
 * @description Stores and retrieves last known product from cookie named `lastKnownProduct`
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class LastKnownProductService {
    public readonly UNKNOWN = 'unknown';

    /** @internal */
    get update(): Observable<LastKnownProduct> {
        return this.updateEvents;
    }

    private readonly updateEvents = new Subject<LastKnownProduct>();

    private readonly cookie = inject(CookieService);
    private readonly config = inject(LastKnownProductConfig);
    private readonly productService = inject(ProductService);
    private readonly urlService = inject(UrlService);

    /** @internal */
    add(lastKnownProduct: LastKnownProduct) {
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + 30);

        this.cookie.putObject(CookieName.LastKnownProduct, lastKnownProduct, { expires: expireDate });
        this.updateEvents.next(lastKnownProduct);
    }

    /** Gets last known product. Returns the value of proxy item MobileLogin-v1.0/Links/BackToProduct. */
    get(): LastKnownProduct {
        const lkp = this.cookie.getObject(CookieName.LastKnownProduct) as LastKnownProduct;

        return (
            lkp || {
                name: this.UNKNOWN,
                url: this.productService.isSingleDomainApp ? this.urlService.current().baseUrl() : this.config.url,
                previous: this.UNKNOWN,
                platformProductId: '',
            }
        );
    }
}
